import { useState } from "react";

import {
  EPISODE_ACTION_RESPONSE_STATUS,
  EPISODE_DETAIL_VIEW_ACTION,
  postEpisodeAction,
} from "@/api/endpoints/channels";
import {
  TRANSCRIPT_FAILED_STATUSES,
  TRANSCRIPT_IN_PROGRESS_STATUSES,
  TRANSCRIPT_SUCCESS_STATUSES,
  TRANSCRIPTION_STATUS,
  TranscriptionStatus,
  TranscriptStatus,
} from "@/constants";
import Button from "@components/Button";

import StatusLegend, { StatusColor } from "../../StatusLegend";
import {
  colorByTranscriptionStatus,
  textByTranscriptionStatus,
} from "../../utils";

import styles from "./TranscriptStatusCell.module.css";

type TranscriptStatusCellProps = {
  episode_id: string;
  transcript_status?: TranscriptStatus;
};

const buttonTextByTranscriptionStatus = {
  indexed: "Transcribe again",
  queued: "Transcribing...",
  failed: "Transcribe episode",
  no_transcript: "Transcribe episode",
};

function getTranscriptionStatus(
  transcript_status?: TranscriptStatus
): TranscriptionStatus | "no_transcript" {
  if (TRANSCRIPT_SUCCESS_STATUSES.find((value) => value === transcript_status))
    return "indexed";

  if (
    TRANSCRIPT_IN_PROGRESS_STATUSES.find((value) => value === transcript_status)
  )
    return "queued";

  if (TRANSCRIPT_FAILED_STATUSES.find((value) => value === transcript_status))
    return "failed";

  return "no_transcript";
}

const TranscriptStatusCell: React.FunctionComponent<
  TranscriptStatusCellProps
> = ({ episode_id, transcript_status }) => {
  const [status, setStatus] = useState<TranscriptionStatus | "no_transcript">(
    getTranscriptionStatus(transcript_status)
  );
  const [statusColor, setStatusColor] = useState<StatusColor>(
    colorByTranscriptionStatus[status] as StatusColor
  );
  const [statusText, setStatusText] = useState<string>(
    textByTranscriptionStatus[status]
  );
  const [buttonText, setButtonText] = useState<string>(
    buttonTextByTranscriptionStatus[status]
  );

  const transcribeEpisode = () => {
    return postEpisodeAction(
      episode_id,
      EPISODE_DETAIL_VIEW_ACTION.TRANSCRIBE
    ).then((response) => {
      if (
        response.ok &&
        response.data &&
        response.data.status ===
          EPISODE_ACTION_RESPONSE_STATUS.TRANSCRIPTION_STARTED
      ) {
        setStatus(TRANSCRIPTION_STATUS.QUEUED);
        setStatusColor(
          colorByTranscriptionStatus[TRANSCRIPTION_STATUS.QUEUED] as StatusColor
        );
        setStatusText(textByTranscriptionStatus[TRANSCRIPTION_STATUS.QUEUED]);
        setButtonText(
          buttonTextByTranscriptionStatus[TRANSCRIPTION_STATUS.QUEUED]
        );
      } else {
        throw new Error(response.statusText);
      }
    });
  };

  return (
    <div>
      <div className={styles.status}>
        <StatusLegend color={statusColor} size="large" />
      </div>
      <p className={styles.status}>{statusText}</p>
      <Button
        className={styles.button}
        onClick={() => transcribeEpisode()}
        disabled={status === TRANSCRIPTION_STATUS.QUEUED}
        size="sm"
        variant="secondary"
      >
        {buttonText}
      </Button>
    </div>
  );
};

export default TranscriptStatusCell;
